import { useCallback, useEffect, useState } from "react";
import { translate } from "@/i18n";
import { inputBarMessages } from "@/i18n/messages/input-bar";
import { learnedContext } from "@/desktop/dictation-vocabulary-native";
import { useDictationVocabularyStore } from "@/stores/dictation-vocabulary-store";
import {
  dictatedSubstitution,
  type DictatedSnapshot,
} from "./dictated-word-substitution";

/** "Remember X for voice?" after a dictated word was corrected in the draft. */
export type VoiceRememberOffer = {
  word: string;
  /** Set after Remember failed; the offer stays so it can be tried again. */
  error: string | null;
  onRemember: () => void;
  onDismiss: () => void;
};

/** How long the draft must stay unchanged before an edit counts: the offer
 *  never shows a word that is still being typed ("Remember D"). */
export const REMEMBER_SETTLE_MS = 700;

/** Watches a composer's draft after a final transcript (`dictated`) and
 *  offers to remember a corrected dictated word. The offer clears when the
 *  draft empties (sent), on Remember, on dismiss and on `clear`. */
export function useVoiceRememberOffer(value: string): {
  offer: VoiceRememberOffer | null;
  /** The draft and transcript span right after a final transcript. */
  setDictated: (snapshot: DictatedSnapshot) => void;
  clear: () => void;
} {
  const [dictated, setDictatedState] = useState<DictatedSnapshot | null>(null);
  const [settled, setSettled] = useState(value);
  const [remembering, setRemembering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clear = useCallback(() => {
    setDictatedState(null);
    setError(null);
  }, []);
  const setDictated = useCallback((snapshot: DictatedSnapshot) => {
    setDictatedState(snapshot);
    setError(null);
  }, []);
  if (dictated && value === "") clear();

  useEffect(() => {
    if (!dictated) return;
    const timer = setTimeout(() => setSettled(value), REMEMBER_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [dictated, value]);

  const substitution =
    dictated && !remembering && settled === value
      ? dictatedSubstitution(dictated, value)
      : null;
  if (!dictated || !substitution) {
    return { offer: null, setDictated, clear };
  }
  return {
    offer: {
      word: substitution.word,
      error,
      onRemember: () => {
        setRemembering(true);
        setError(null);
        learnedContext(dictated.text, substitution.from, substitution.to)
          .then((context) =>
            useDictationVocabularyStore.getState().learn({
              word: substitution.word,
              ...context,
              surface: "composer",
            }),
          )
          .then(clear, () =>
            setError(translate(inputBarMessages, "rememberFailed")),
          )
          .finally(() => setRemembering(false));
      },
      onDismiss: clear,
    },
    setDictated,
    clear,
  };
}
